import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Paper,
  ButtonGroup,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import StatusBadge from "../components/ReasuableFuncations/StatusBadge";
import apiService from "../components/ReasuableFuncations/api";
import ProjectFilter from "../components/filter/ProjectFilter";

const ProjectList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [status, setStatus] = useState("All");
  const [country, setCountry] = useState("All");
  const [organization, setOrganization] = useState("All");
  const [fundingType, setFundingType] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [thematicArea, setThematicArea] = useState("Total");

  const [fundingOptions] = useState([
    "State Contributions",
    "Public Sector",
    "Public - Private Partnership", 
    "Development Partner"
  ]);

  const thematicAreas = [
    "Total",
    "Infrastructure",
    "Legal and Regulatory Framework",
    "ICT Services and Digital Skills",
    "Climate Action",
    "Financial Stability",
    "Others",
  ];

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fundingType, status, startDate, endDate, organization, country, thematicArea]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      setUserRole({ selectedOption, regionalNetworkOption, memberStateOption, countryOption });
    }
  }, []);

  const fetchProjects = async () => {
    const params = new URLSearchParams();

    // Only keep the filter logic for basic filters
    const filters = {
      status,
      fundingType,
      organization,
      country,
      startDate,
      endDate
    };

    // Only add non-"All" and non-empty values to params
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "All") {
        params.append(key, value);
      }
    });

    try {
      const queryString = params.toString();
      const url = queryString ? `/projects?${queryString}` : '/projects';
      let response;
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData) {
        throw new Error('User data not found');
      }

      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      
      switch (selectedOption) {
        case 'Comesa':
          response = await apiService.get(url);
          break;

        case 'Regional Network':
          if (regionalNetworkOption) {
            response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}`);
          }
          break;

        case 'Member State':
          if (memberStateOption) {
            response = await apiService.get(`${url}?country=${countryOption}`);
          }
          break;

        default:
          throw new Error('Invalid selection');
      }

      if (response.data) {
        if (thematicArea === "Total") {
          setProjects(response.data);
        } else {
          const filteredProjects = response.data.filter(
            project => project.climate === thematicArea
          );
          setProjects(filteredProjects);
        }
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects. Please try again later.");
    }
  };

  const clearFilters = () => {
    setCountry("All");
    setOrganization("All");
    setStatus("All");
    setFundingType("All");
    setStartDate("");
    setEndDate("");
  };

  const handleOpenDialog = (description) => {
    setSelectedDescription(description);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDescription("");
  };

  const handleEdit = (project) => {
    navigate("/edit-project", { state: { project } });
  };

  const handleDeleteConfirmation = (projectId) => {
    setProjectToDelete(projectId);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (projectToDelete) {
      try {
        const response = await apiService.delete(
          `/api/projects/${projectToDelete}`
        );

        if (response.statusText !== "OK") {
          throw new Error("Failed to delete the project");
        }

        setProjects((prevProjects) =>
          prevProjects.filter((project) => project._id !== projectToDelete)
        );
        setSnackbarMessage("Project deleted successfully!");
        setSnackbarSeverity("success");
        setOpenDeleteDialog(false);
        setProjectToDelete(null);
      } catch (err) {
        console.error("Failed to delete the project:", err);
        setSnackbarMessage("Error deleting project.");
        setSnackbarSeverity("error");
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setProjectToDelete(null);
  };

  const canEditDelete = (project) => {
    if (!userRole) return false;
    
    switch (userRole.selectedOption) {
      case 'Comesa':
        return true; // Comesa users can edit/delete all projects
      case 'Regional Network':
        return project.organization === userRole.regionalNetworkOption;
      case 'Member State':
        return project.countryOption === userRole.countryOption;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ width: "100%", backgroundColor: "white", margin: "auto", p: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Project List
      </Typography>

      {error && (
        <Typography variant="body1" color="error" align="center">
          {error}
        </Typography>
      )}

      <Paper>
        <ProjectFilter
          status={status}
          organization={organization}
          setOrganization={setOrganization}
          country={country}
          setCountry={setCountry}
          setStatus={setStatus}
          fundingType={fundingType}
          setFundingType={setFundingType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          handleClearFilters={clearFilters}
          fundingOptions={fundingOptions}
        />
        
        {/* Add Thematic Area Filter Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <ButtonGroup variant="outlined" aria-label="thematic area filter">
            {thematicAreas.map((area) => (
              <Button
                key={area}
                onClick={() => setThematicArea(area)}
                variant={thematicArea === area ? "contained" : "outlined"}
                sx={{ 
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {area}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel>Project Name</TableSortLabel>
                </TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Funding Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>ICT Thematic Area</TableCell>
                <TableCell>Collaborating Regions</TableCell>
                <TableCell>Collaborating States</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project._id}>
                  <TableCell>{project.projectName}</TableCell>
                  <TableCell>
                    {new Date(project.startDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(project.endDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={project.status} />
                  </TableCell>
                  <TableCell>{project.funding}</TableCell>
                  <TableCell>
                    {project.description.split(" ").length > 11
                      ? `${project.description
                          .split(" ")
                          .slice(0, 11)
                          .join(" ")}...`
                      : project.description}
                    {project.description.split(" ").length > 11 && (
                      <Button
                        size="small"
                        onClick={() => handleOpenDialog(project.description)}
                      >
                        More
                      </Button>
                    )}
                  </TableCell>

                  <TableCell>
                    {project.climate}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(project.collaboratingRegions) 
                      ? project.collaboratingRegions.join(', ')
                      : ''}
                  </TableCell>
                  <TableCell>
                    {Array.isArray(project.collaboratingStates)
                      ? project.collaboratingStates.map(state => state.name).join(', ')
                      : ''}
                  </TableCell>
                  <TableCell>
                    {canEditDelete(project) && (
                      <>
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleEdit(project)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteConfirmation(project._id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>Description</DialogTitle>
          <DialogContent>
            <Typography variant="body1">{selectedDescription}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Are you sure you want to delete this project?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarMessage !== ""}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage("")}
          message={snackbarMessage}
          severity={snackbarSeverity}
          sx={{
            "& .MuiSnackbarContent-root": {
              backgroundColor: snackbarSeverity === "success" ? "green" : "red", // Set the background color based on severity
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ProjectList;
